import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Skeleton,
  Button,
  IconButton,
  Collapse,
  Chip,
  Divider,
  TextField,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import AssignmentIcon from '@mui/icons-material/Assignment';
import MedicationIcon from '@mui/icons-material/Medication';
import AssessmentIcon from '@mui/icons-material/Assessment';
import FlagIcon from '@mui/icons-material/Flag';
import { get, post } from '@/utils/apiClient';
import { logger } from '@/utils/logger';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface PatientSummary {
  patient_id: string;
  patient_name: string;
  preferred_name?: string | null;
  pronouns?: string | null;
  age?: number | null;
  gender?: string | null;
  diagnoses: Array<{ code: string; description: string; is_primary: boolean }>;
  screenings: Array<{
    screening_type: string;
    score: number;
    max_score?: number | null;
    interpretation?: string | null;
    date: string;
  }>;
  medications: Array<{ name: string; dosage: string; frequency: string }>;
  objectives: Array<{
    description: string;
    status: string;
    target_date?: string | null;
  }>;
  recent_sessions: number;
}

interface SearchResult {
  patient_id: string;
  patient_name: string;
  preferred_name?: string | null;
}

interface AIPlannedAction {
  id: string;
  description: string;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
}

export interface PatientContextPanelProps {
  /** Currently selected patient from AI or manual selection */
  currentPatient: { patient_id: string; patient_name: string } | null;
  /** Session ID for setting patient in backend state */
  sessionId: string | null;
  /** Callback when patient is manually changed */
  onPatientChange?: (patient: { patient_id: string; patient_name: string } | null) => void;
  /** AI planned actions (extensible — future providers can add their own) */
  plannedActions?: AIPlannedAction[];
}

/* ------------------------------------------------------------------ */
/*  Main Component                                                     */
/* ------------------------------------------------------------------ */

export default function PatientContextPanel({
  currentPatient,
  sessionId,
  onPatientChange,
  plannedActions = [],
}: PatientContextPanelProps) {
  const [summary, setSummary] = useState<PatientSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSearch, setShowSearch] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const [setPatientError, setSetPatientError] = useState<string | null>(null);

  // Fetch patient summary when currentPatient changes
  const fetchSummary = useCallback(async (patientId: string, signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      const res = await get(`/api/context-panel/patient/${patientId}`, { signal });
      if (res && res.ok) {
        const data = await res.json();
        setSummary(data);
      } else {
        setError('Could not load patient data');
      }
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        logger.error('PatientContextPanel: failed to fetch summary', err);
        setError('Failed to load patient context');
      }
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    const abortController = new AbortController();
    if (currentPatient?.patient_id) {
      fetchSummary(currentPatient.patient_id, abortController.signal);
    } else {
      setSummary(null);
    }
    return () => abortController.abort();
  }, [currentPatient?.patient_id, fetchSummary, retryKey]);

  const handlePatientSelect = useCallback(
    async (patient: SearchResult) => {
      setShowSearch(false);
      const newPatient = {
        patient_id: patient.patient_id,
        patient_name: patient.patient_name,
      };

      // Optimistically update the UI
      onPatientChange?.(newPatient);

      // Persist to backend session state
      setSetPatientError(null);
      if (sessionId) {
        try {
          const res = await post('/api/context-panel/set-patient', {
            session_id: sessionId,
            patient_id: patient.patient_id,
            patient_name: patient.patient_name,
          });
          if (res && res.status === 409) {
            setSetPatientError('AI is processing — patient will sync on next turn.');
          } else if (res && !res.ok) {
            setSetPatientError('Could not save patient selection.');
          }
        } catch (err) {
          logger.error('PatientContextPanel: failed to set patient in session', err);
          setSetPatientError('Could not save patient selection.');
        }
      }
    },
    [sessionId, onPatientChange],
  );

  /* ---- No patient selected ---- */
  if (!currentPatient) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <PanelHeader onSearchToggle={() => setShowSearch(!showSearch)} />
        {showSearch ? (
          <PatientSearch onSelect={handlePatientSelect} onClose={() => setShowSearch(false)} />
        ) : (
          <EmptyState onSearch={() => setShowSearch(true)} />
        )}
      </Box>
    );
  }

  /* ---- Loading ---- */
  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <PanelHeader onSearchToggle={() => setShowSearch(!showSearch)} />
        <PatientSkeleton />
      </Box>
    );
  }

  /* ---- Error ---- */
  if (error && !summary) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <PanelHeader onSearchToggle={() => setShowSearch(!showSearch)} />
        <PanelError message={error} onRetry={() => setRetryKey((k) => k + 1)} />
      </Box>
    );
  }

  /* ---- Normal view ---- */
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PanelHeader onSearchToggle={() => setShowSearch(!showSearch)} />

      {showSearch && (
        <PatientSearch onSelect={handlePatientSelect} onClose={() => setShowSearch(false)} />
      )}

      {setPatientError && (
        <Box sx={{ px: 2, py: 0.5 }}>
          <Typography variant="caption" color="warning.main" sx={{ fontSize: '0.75rem' }}>
            {setPatientError}
          </Typography>
        </Box>
      )}

      {summary && (
        <>
          <PatientHeaderSection summary={summary} onChangePatient={() => setShowSearch(true)} />
          <CollapsibleSection title="Diagnoses" icon={<FlagIcon fontSize="small" />} defaultOpen>
            <DiagnosesList diagnoses={summary.diagnoses} />
          </CollapsibleSection>
          <CollapsibleSection title="Recent Screenings" icon={<AssessmentIcon fontSize="small" />} defaultOpen>
            <ScreeningsList screenings={summary.screenings} />
          </CollapsibleSection>
          <CollapsibleSection title="Medications" icon={<MedicationIcon fontSize="small" />}>
            <MedicationsList medications={summary.medications} />
          </CollapsibleSection>
          <CollapsibleSection title="Treatment Objectives" icon={<AssignmentIcon fontSize="small" />}>
            <ObjectivesList objectives={summary.objectives} />
          </CollapsibleSection>
          {plannedActions.length > 0 && (
            <CollapsibleSection title="AI Planned Actions" icon={<AssignmentIcon fontSize="small" />} defaultOpen>
              <PlannedActionsList actions={plannedActions} />
            </CollapsibleSection>
          )}
        </>
      )}
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/*  Panel Header                                                       */
/* ------------------------------------------------------------------ */

function PanelHeader({ onSearchToggle }: { onSearchToggle: () => void }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 2,
        py: 1.5,
        borderBottom: '1px solid var(--tile-border, #e5e7eb)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PersonIcon fontSize="small" sx={{ color: 'var(--c1, #57bde4)' }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
          Patient Context
        </Typography>
      </Box>
      <IconButton size="small" onClick={onSearchToggle} title="Search / change patient">
        <SearchIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/*  Patient Header Section                                             */
/* ------------------------------------------------------------------ */

function PatientHeaderSection({
  summary,
  onChangePatient,
}: {
  summary: PatientSummary;
  onChangePatient: () => void;
}) {
  const demographics: string[] = [];
  if (summary.age != null) demographics.push(`${summary.age}y`);
  if (summary.gender) demographics.push(summary.gender);
  if (summary.pronouns) demographics.push(summary.pronouns);

  return (
    <Box
      sx={{
        px: 2,
        py: 1.5,
        background:
          'linear-gradient(135deg, rgba(var(--c1-rgb, 87, 189, 228), 0.06) 0%, rgba(var(--c2-rgb, 102, 179, 43), 0.04) 100%)',
        borderBottom: '1px solid var(--tile-border, #e5e7eb)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '0.95rem', color: 'text.primary' }}>
          {summary.patient_name}
          {summary.preferred_name && (
            <Typography
              component="span"
              variant="body2"
              sx={{ fontWeight: 400, color: 'text.secondary', ml: 0.5, fontSize: '0.82rem' }}
            >
              ({summary.preferred_name})
            </Typography>
          )}
        </Typography>
        <IconButton size="small" onClick={onChangePatient} title="Change patient">
          <SwapHorizIcon fontSize="small" />
        </IconButton>
      </Box>
      {demographics.length > 0 && (
        <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary', mt: 0.25 }}>
          {demographics.join(' · ')}
        </Typography>
      )}
      <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary', mt: 0.25 }}>
        {summary.recent_sessions} session{summary.recent_sessions !== 1 ? 's' : ''} in last 30 days
      </Typography>
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/*  Collapsible Section                                                */
/* ------------------------------------------------------------------ */

function CollapsibleSection({
  title,
  icon,
  defaultOpen = false,
  children,
}: {
  title: string;
  icon?: React.ReactNode;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 0.75,
          cursor: 'pointer',
          '&:hover': { bgcolor: 'action.hover' },
        }}
        onClick={() => setOpen(!open)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          {icon}
          <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
            {title}
          </Typography>
        </Box>
        {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
      </Box>
      <Collapse in={open}>
        <Box sx={{ px: 2, pb: 1 }}>{children}</Box>
      </Collapse>
      <Divider />
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  Data sections                                                      */
/* ------------------------------------------------------------------ */

function DiagnosesList({ diagnoses }: { diagnoses: PatientSummary['diagnoses'] }) {
  if (!diagnoses.length) return <EmptyData label="No active diagnoses" />;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {diagnoses.map((d, i) => (
        <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
          <Chip
            label={d.code}
            size="small"
            color={d.is_primary ? 'primary' : 'default'}
            sx={{ fontSize: '0.7rem', height: 20, minWidth: 0 }}
          />
          <Typography variant="body2" sx={{ fontSize: '0.78rem', lineHeight: 1.4 }}>
            {d.description}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function ScreeningsList({ screenings }: { screenings: PatientSummary['screenings'] }) {
  if (!screenings.length) return <EmptyData label="No recent screenings" />;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {screenings.map((s, i) => (
        <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" sx={{ fontSize: '0.78rem' }}>
            {s.screening_type}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Chip
              label={`${s.score}${s.max_score ? `/${s.max_score}` : ''}`}
              size="small"
              sx={{ fontSize: '0.7rem', height: 20 }}
            />
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
              {s.date}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  );
}

function MedicationsList({ medications }: { medications: PatientSummary['medications'] }) {
  if (!medications.length) return <EmptyData label="No active medications" />;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {medications.map((m, i) => (
        <Typography key={i} variant="body2" sx={{ fontSize: '0.78rem' }}>
          <strong>{m.name}</strong> — {m.dosage}, {m.frequency}
        </Typography>
      ))}
    </Box>
  );
}

function ObjectivesList({ objectives }: { objectives: PatientSummary['objectives'] }) {
  if (!objectives.length) return <EmptyData label="No active objectives" />;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {objectives.map((o, i) => (
        <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
          <Chip
            label={o.status}
            size="small"
            color={o.status === 'active' ? 'success' : 'default'}
            sx={{ fontSize: '0.65rem', height: 18, textTransform: 'capitalize' }}
          />
          <Typography variant="body2" sx={{ fontSize: '0.78rem', lineHeight: 1.4 }}>
            {o.description}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function PlannedActionsList({ actions }: { actions: AIPlannedAction[] }) {
  if (!actions.length) return <EmptyData label="No planned actions" />;
  const statusColor = (s: string) => {
    if (s === 'completed') return 'success';
    if (s === 'failed') return 'error';
    if (s === 'in_progress') return 'warning';
    return 'default';
  };
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {actions.map((a) => (
        <Box key={a.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
          <Chip
            label={a.status.replace('_', ' ')}
            size="small"
            color={statusColor(a.status) as 'success' | 'error' | 'warning' | 'default'}
            sx={{ fontSize: '0.65rem', height: 18, textTransform: 'capitalize' }}
          />
          <Typography variant="body2" sx={{ fontSize: '0.78rem', lineHeight: 1.4 }}>
            {a.description}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function EmptyData({ label }: { label: string }) {
  return (
    <Typography variant="body2" sx={{ fontSize: '0.78rem', color: 'text.disabled', fontStyle: 'italic' }}>
      {label}
    </Typography>
  );
}

/* ------------------------------------------------------------------ */
/*  Patient Search                                                     */
/* ------------------------------------------------------------------ */

function PatientSearch({
  onSelect,
  onClose,
}: {
  onSelect: (patient: SearchResult) => void;
  onClose: () => void;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const abortController = new AbortController();
    const timeoutId = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await get(`/api/context-panel/patient-search?q=${encodeURIComponent(query)}`, { signal: abortController.signal });
        if (res && res.ok) {
          const data = await res.json();
          setResults(data);
        }
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          logger.error('PatientSearch: search failed', err);
        }
      } finally {
        if (!abortController.signal.aborted) {
          setSearching(false);
        }
      }
    }, 300);
    return () => {
      clearTimeout(timeoutId);
      abortController.abort();
    };
  }, [query]);

  return (
    <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid var(--tile-border, #e5e7eb)' }}>
      <TextField
        fullWidth
        size="small"
        placeholder="Search patients..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        autoFocus
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton size="small" onClick={onClose} aria-label="Close">
                <CloseIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
          sx: { fontSize: '0.82rem' },
        }}
      />
      {searching && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
          <CircularProgress size={20} />
        </Box>
      )}
      {!searching && results.length > 0 && (
        <List dense sx={{ maxHeight: 200, overflow: 'auto', mt: 0.5 }}>
          {results.map((r) => (
            <ListItemButton key={r.patient_id} onClick={() => onSelect(r)} sx={{ borderRadius: 1, py: 0.5 }}>
              <ListItemText
                primary={r.patient_name}
                secondary={r.preferred_name ? `"${r.preferred_name}"` : undefined}
                primaryTypographyProps={{ fontSize: '0.82rem' }}
                secondaryTypographyProps={{ fontSize: '0.72rem' }}
              />
            </ListItemButton>
          ))}
        </List>
      )}
      {!searching && query && results.length === 0 && (
        <Typography variant="body2" sx={{ fontSize: '0.78rem', color: 'text.secondary', py: 1, textAlign: 'center' }}>
          No patients found
        </Typography>
      )}
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/*  Empty state / Loading / Error                                      */
/* ------------------------------------------------------------------ */

function EmptyState({ onSearch }: { onSearch: () => void }) {
  return (
    <Box sx={{ px: 2, py: 4, textAlign: 'center', flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      <PersonIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5, fontSize: '0.82rem' }}>
        No patient selected
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.disabled', mb: 2, px: 1 }}>
        Mention a patient in the chat or search to select one manually
      </Typography>
      <Button
        size="small"
        variant="outlined"
        startIcon={<SearchIcon />}
        onClick={onSearch}
        sx={{ textTransform: 'none', borderColor: 'var(--c1, #57bde4)', color: 'var(--c1-dark, #2c8db7)' }}
      >
        Search patients
      </Button>
    </Box>
  );
}

function PatientSkeleton() {
  return (
    <Box sx={{ px: 2, py: 2 }}>
      <Skeleton variant="text" width="60%" height={24} sx={{ mb: 0.5 }} />
      <Skeleton variant="text" width="40%" height={18} sx={{ mb: 1.5 }} />
      <Skeleton variant="rounded" width="100%" height={32} sx={{ mb: 1 }} />
      <Skeleton variant="rounded" width="100%" height={32} sx={{ mb: 1 }} />
      <Skeleton variant="rounded" width="100%" height={60} sx={{ mb: 1 }} />
      <Skeleton variant="rounded" width="100%" height={60} />
    </Box>
  );
}

function PanelError({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontSize: '0.82rem' }}>
        {message}
      </Typography>
      {onRetry && (
        <Button
          size="small"
          variant="outlined"
          onClick={onRetry}
          sx={{ textTransform: 'none', borderColor: 'var(--c1, #57bde4)', color: 'var(--c1-dark, #2c8db7)' }}
        >
          Try again
        </Button>
      )}
    </Box>
  );
}
